import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Send, ArrowLeft, CheckCircle, Trophy,
  Mic, MicOff, Video, VideoOff, PhoneOff, FileText,
  Volume2, VolumeX
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const BG_IMG = 'https://images.unsplash.com/photo-1757405960576-0698c7a8717c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMGNvYWNoaW5nJTIwc2Vzc2lvbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc5MjYzMTExfDA&ixlib=rb-4.1.0&q=85';

const EMOTION_BADGE_STYLES = {
  angry: 'bg-red-500/15 text-red-400',
  anxious: 'bg-amber-500/15 text-amber-400',
  pleased: 'bg-emerald-500/15 text-emerald-400',
  confused: 'bg-purple-500/15 text-purple-400',
  defensive: 'bg-orange-500/15 text-orange-400',
  empathetic: 'bg-sky-500/15 text-sky-400',
  hesitant: 'bg-yellow-500/15 text-yellow-300',
};

const EMOTION_CONFIG = {
  neutral:   { color: 'border-gray-400',    glow: '',                         label: 'Neutral',   ring: 'ring-gray-400/20' },
  angry:     { color: 'border-red-500',      glow: 'shadow-red-500/30',        label: 'Frustrated', ring: 'ring-red-500/30' },
  anxious:   { color: 'border-amber-400',    glow: 'shadow-amber-400/30',      label: 'Anxious',   ring: 'ring-amber-400/30' },
  pleased:   { color: 'border-emerald-400',  glow: 'shadow-emerald-400/30',    label: 'Pleased',   ring: 'ring-emerald-400/30' },
  confused:  { color: 'border-purple-400',   glow: 'shadow-purple-400/30',     label: 'Confused',  ring: 'ring-purple-400/30' },
  defensive: { color: 'border-orange-500',   glow: 'shadow-orange-500/30',     label: 'Defensive', ring: 'ring-orange-500/30' },
  empathetic:{ color: 'border-sky-400',      glow: 'shadow-sky-400/30',        label: 'Empathetic',ring: 'ring-sky-400/30' },
  hesitant:  { color: 'border-yellow-300',   glow: 'shadow-yellow-300/20',     label: 'Hesitant',  ring: 'ring-yellow-300/20' },
};

const LANG_NAMES = {
  en: 'English', hi: 'हिन्दी', ta: 'தமிழ்', te: 'తెలుగు', kn: 'ಕನ್ನಡ',
  ml: 'മലയാളം', mr: 'मराठी', bn: 'বাংলা', gu: 'ગુજરાતી', pa: 'ਪੰਜਾਬੀ',
};

export default function SimulationChat() {
  const { simId } = useParams();
  const navigate = useNavigate();
  const { axiosAuth, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [simulation, setSimulation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [speechSupported] = useState(!!SpeechRecognition);
  const [micOn, setMicOn] = useState(true);
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [vidOn, setVidOn] = useState(true);
  const [currentEmotion, setCurrentEmotion] = useState('neutral');
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const webcamRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const animFrameRef = useRef(null);
  const ttsAudioRef = useRef(null);
  const isRecordingRef = useRef(false);

  // Stop TTS audio
  const stopTTS = useCallback(() => {
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current.currentTime = 0;
      ttsAudioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  // Play TTS for text
  const playTTS = useCallback(async (text) => {
    if (!ttsEnabled || !text) return;
    stopTTS();
    try {
      const res = await axiosAuth().post('/tts', { text, voice: simulation?.tts_voice || 'nova', speed: 1.0 });
      if (res.data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        ttsAudioRef.current = audio;
        setIsSpeaking(true);
        audio.onended = () => { setIsSpeaking(false); ttsAudioRef.current = null; };
        audio.onerror = () => { setIsSpeaking(false); ttsAudioRef.current = null; };
        await audio.play();
      }
    } catch (err) {
      logger_warn('TTS failed', err);
      setIsSpeaking(false);
    }
  }, [ttsEnabled, axiosAuth, stopTTS, simulation?.tts_voice]);

  useEffect(() => {
    const fetchSim = async () => {
      try {
        const res = await axiosAuth().get(`/simulations/${simId}`);
        setSimulation(res.data.simulation);
        const msgs = res.data.simulation.messages || [];
        setMessages(msgs);
        if (res.data.simulation.status === 'completed' || res.data.simulation.status === 'scoring' || res.data.simulation.status === 'failed') { setShowComplete(true); }
        // Set initial emotion from last AI message
        const lastAI = [...msgs].reverse().find(m => m.role === 'assistant');
        if (lastAI?.emotion) setCurrentEmotion(lastAI.emotion);
      } catch { toast.error('Failed to load'); navigate('/'); }
    };
    fetchSim();
  }, [simId, axiosAuth, navigate]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (vidOn) {
      navigator.mediaDevices?.getUserMedia({ video: true })
        .then(s => { streamRef.current = s; if (webcamRef.current) webcamRef.current.srcObject = s; })
        .catch((err) => { console.warn('Webcam access denied or unavailable:', err?.message); });
    } else {
      streamRef.current?.getTracks().forEach(t => t.stop());
    }
    return () => { streamRef.current?.getTracks().forEach(t => t.stop()); };
  }, [vidOn]);

  useEffect(() => {
    if (!speechSupported) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = simulation?.stt_code || 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onresult = (e) => {
      let interim = '', final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      if (final) setInputText(prev => prev + (prev ? ' ' : '') + final);
      setInterimText(interim);
    };
    recognition.onerror = (e) => {
      if (e.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone permission in your browser settings and reload the page.');
        setIsRecording(false); setInterimText(''); setVoiceLevel(0);
      } else if (e.error === 'no-speech' || e.error === 'aborted') {
        // Silently handle — auto-restart will handle continuation
      } else {
        toast.error(`Voice error: ${e.error}`);
        setIsRecording(false); setInterimText(''); setVoiceLevel(0);
      }
    };
    // Auto-restart on unexpected end (browser stops after silence periods)
    recognition.onend = () => {
      // Only auto-restart if user hasn't explicitly stopped recording
      if (isRecordingRef.current) {
        try {
          recognition.start();
        } catch (err) {
          console.warn('Speech recognition restart failed:', err?.message);
          // Already started or permission issue - stop gracefully
          setIsRecording(false); setInterimText(''); setVoiceLevel(0);
          if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        }
      } else {
        setIsRecording(false); setInterimText(''); setVoiceLevel(0);
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      }
    };
    recognitionRef.current = recognition;
    return () => { recognition.abort(); };
  }, [speechSupported, simulation?.stt_code]);

  // Cleanup TTS on unmount
  useEffect(() => { return () => { stopTTS(); }; }, [stopTTS]);

  const startVoiceMonitor = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new AudioContext(); audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser(); analyser.fftSize = 64;
      src.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => { analyser.getByteFrequencyData(dataArray); setVoiceLevel(Math.min(dataArray.reduce((a, b) => a + b, 0) / dataArray.length / 80, 1)); animFrameRef.current = requestAnimationFrame(tick); };
      tick();
    } catch (e) { console.warn('Voice monitor unavailable:', e?.message); }
  }, []);

  const stopVoiceMonitor = useCallback(() => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    audioCtxRef.current?.close();
    setVoiceLevel(0);
  }, []);

  const toggleRecording = useCallback(async () => {
    if (!speechSupported) { toast.error('Speech recognition not supported. Try Chrome or Edge.'); return; }
    // Interrupt TTS when user starts speaking
    stopTTS();
    if (isRecording) {
      isRecordingRef.current = false;
      recognitionRef.current?.stop();
      stopVoiceMonitor();
      setIsRecording(false);
    } else {
      // Check microphone permission first
      try {
        const permStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Release immediately so SpeechRecognition can use the mic
        permStream.getTracks().forEach(t => t.stop());
      } catch (permErr) {
        toast.error('Microphone access denied. Please allow microphone permission in your browser settings and reload.');
        return;
      }
      try {
        setInterimText('');
        isRecordingRef.current = true;
        recognitionRef.current?.start();
        setIsRecording(true);
        // Start voice level monitor after recognition is running
        startVoiceMonitor();
      } catch (err) {
        toast.error('Could not start voice recognition. Please try again.');
        isRecordingRef.current = false;
        setIsRecording(false);
      }
    }
  }, [speechSupported, isRecording, startVoiceMonitor, stopVoiceMonitor, stopTTS]);

  const sendMessage = async () => {
    if (!inputText.trim() || sending) return;
    // Stop recording if active
    if (isRecordingRef.current) {
      isRecordingRef.current = false;
      recognitionRef.current?.stop();
      stopVoiceMonitor();
      setIsRecording(false);
    }
    // Interrupt TTS when user sends
    stopTTS();
    const userMsg = inputText.trim(); setInputText(''); setSending(true);
    setMessages(prev => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    try {
      const res = await axiosAuth().post(`/simulations/${simId}/message`, { message: userMsg });
      setMessages(prev => [...prev.slice(0, -1), res.data.user_message, res.data.ai_message]);
      // Update emotion
      if (res.data.ai_message?.emotion) setCurrentEmotion(res.data.ai_message.emotion);
      // Play TTS for AI response
      if (res.data.ai_message?.content) playTTS(res.data.ai_message.content);
    } catch (err) {
      const isTimeout = err.response?.status === 504;
      setMessages(prev => prev.slice(0, -1));
      setInputText(userMsg);
      if (isTimeout) {
        toast.error('The AI is taking too long to respond. Please try again.', {
          duration: 8000,
          action: { label: 'Retry', onClick: () => { setInputText(userMsg); setTimeout(() => sendMessage(), 100); } },
        });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    }
    finally { setSending(false); inputRef.current?.focus(); }
  };

  const completeSimulation = async () => {
    stopTTS();
    setCompleting(true);
    try {
      await axiosAuth().post(`/simulations/${simId}/complete`);
      setSimulation(prev => ({ ...prev, status: 'completed' }));
      setCompleting(false);
      setShowComplete(true);
    } catch (err) {
      console.error('Complete simulation failed:', err);
      const isTimeout = err.response?.status === 504;
      if (isTimeout) {
        toast.error('Scoring is taking longer than usual. Please try ending the simulation again.', {
          duration: 8000,
          action: { label: 'Retry', onClick: () => completeSimulation() },
        });
      } else {
        toast.error('Failed to end simulation');
      }
      setCompleting(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };
  const userMsgCount = messages.filter(m => m.role === 'user').length;
  const isCompleted = simulation?.status === 'completed' || simulation?.status === 'scoring' || simulation?.status === 'failed' || showComplete;
  const lastAIMsg = [...messages].reverse().find(m => m.role === 'assistant');
  const emotionCfg = EMOTION_CONFIG[currentEmotion] || EMOTION_CONFIG.neutral;

  // Show success completion screen
  if (showComplete) {
    return (
      <div className="dark h-screen flex items-center justify-center bg-gray-950 text-gray-100" data-testid="simulation-complete-screen">
        <div className="max-w-md w-full mx-4 text-center">
          <div className="mb-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/15 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-3">Simulator Completed</h1>
            <p className="font-body text-base text-gray-300 leading-relaxed mb-2">
              Your simulator has been successfully completed.
            </p>
            <p className="font-body text-sm text-gray-400 leading-relaxed mb-2">
              Your personalized report is currently being generated.
            </p>
            <p className="font-body text-sm text-gray-400 leading-relaxed mb-2">
              You will receive an email with a secure link to access your report once it is ready.
            </p>
            <p className="font-body text-sm text-gray-500 leading-relaxed mt-4">
              You may safely close this window.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <button data-testid="go-to-dashboard-btn" onClick={() => navigate('/')}
              className="w-full h-11 bg-emerald-500 text-white rounded-xl font-medium text-sm hover:bg-emerald-600 transition-all flex items-center justify-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
            <button data-testid="go-to-progress-btn" onClick={() => navigate('/progress')}
              className="w-full h-11 bg-white/5 border border-white/10 text-gray-300 rounded-xl font-medium text-sm hover:bg-white/10 transition-all flex items-center justify-center gap-2">
              <Trophy className="w-4 h-4" /> View Progress
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!simulation) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="text-center"><div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-sm text-gray-400">Loading simulation...</p></div>
    </div>
  );

  return (
    <div className="dark h-screen max-h-screen flex flex-col bg-gray-950 text-gray-100 overflow-hidden" data-testid="simulation-chat-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 bg-gray-950/80 backdrop-blur-md z-30 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="back-to-dashboard" onClick={() => { stopTTS(); navigate('/'); }}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all border border-white/10">
            <ArrowLeft className="w-4 h-4 text-gray-300" />
          </button>
          <div className="min-w-0">
            <h2 className="font-heading text-sm sm:text-base font-semibold tracking-tight truncate text-white">{simulation.scenario_title}</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${isCompleted ? 'bg-gray-500' : completing ? 'bg-amber-400 animate-pulse' : 'bg-red-500 animate-pulse'}`} />
                <span className="text-[10px] font-medium tracking-[0.15em] uppercase text-gray-400">{isCompleted ? 'ENDED' : completing ? 'SCORING...' : 'LIVE'}</span>
              </div>
              <Badge className="bg-emerald-500/15 text-emerald-400 border-none text-[10px] px-1.5 py-0">{simulation.difficulty}</Badge>
              {simulation.language && simulation.language !== 'en' && (
                <Badge data-testid="language-badge" className="bg-sky-500/15 text-sky-400 border-none text-[10px] px-1.5 py-0">
                  {LANG_NAMES[simulation.language] || simulation.language_name || simulation.language}
                </Badge>
              )}
              {simulation.variation_title && simulation.variation_title !== 'Default' && (
                <Badge className="bg-amber-500/15 text-amber-400 border-none text-[10px] px-1.5 py-0">{simulation.variation_title}</Badge>
              )}
              {simulation.context?.user_role && (
                <span className="text-[10px] text-gray-500 hidden sm:inline">as {simulation.context.user_role}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* TTS Toggle */}
          <button data-testid="tts-toggle" onClick={() => { if (ttsEnabled) stopTTS(); setTtsEnabled(!ttsEnabled); }}
            className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all ${ttsEnabled ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400' : 'bg-white/5 border-white/10 text-gray-500'}`}
            title={ttsEnabled ? 'TTS On' : 'TTS Off'}>
            {ttsEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          </button>
          {isCompleted && (
            <>
              <Button data-testid="view-report-button" onClick={() => navigate(`/report/${simId}`)}
                className="h-8 px-3 bg-emerald-500 text-white gap-1.5 text-xs font-medium rounded-lg hover:bg-emerald-600">
                <FileText className="w-3.5 h-3.5" /> View Report
              </Button>
            </>
          )}
          {!isCompleted && userMsgCount >= 3 && (
            <Button data-testid="complete-simulation-button" onClick={completeSimulation} disabled={completing}
              className="h-8 px-3 bg-emerald-500 text-white gap-1.5 text-xs font-medium rounded-lg hover:bg-emerald-600 disabled:opacity-50">
              {completing ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Trophy className="w-3.5 h-3.5" />}
              {completing ? 'Analyzing...' : 'End & Score'}
            </Button>
          )}
        </div>
      </div>

      {/* Main Content - Control Room Grid */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-4 lg:p-4 overflow-hidden min-h-0">
        {/* Video Area - 8 cols */}
        <div className="col-span-1 lg:col-span-8 flex flex-col min-h-[200px] lg:min-h-0 relative">
          <div className="flex-1 relative bg-gray-900 lg:rounded-2xl border-0 lg:border lg:border-white/10 overflow-hidden shadow-2xl">
            <img src={BG_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/40 to-gray-900/80" />

            {/* AI Partner Avatar with Emotion */}
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="text-center">
                <div className={`w-28 h-28 sm:w-36 sm:h-36 rounded-2xl flex items-center justify-center mx-auto border-2 shadow-2xl transition-all duration-500 backdrop-blur-sm ${emotionCfg.color} ${emotionCfg.glow} ${isSpeaking ? `ring-4 ${emotionCfg.ring}` : ''} ${sending ? 'border-emerald-400/60 bg-emerald-900/30 shadow-emerald-500/20' : 'bg-gray-800/50'}`}
                  data-testid="avatar-container">
                  {simulation.avatar ? (
                    <img src={simulation.avatar} alt={simulation.avatar_name || 'AI'} className="w-full h-full object-contain p-3 rounded-2xl" />
                  ) : (
                    <span className="font-heading text-4xl sm:text-5xl font-bold text-emerald-400">{(simulation.avatar_name || 'AI')[0]}</span>
                  )}
                </div>

                {/* Name + Emotion Badge */}
                <div className="mt-3 flex items-center justify-center gap-2">
                  <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${sending ? 'bg-amber-400 animate-pulse' : isSpeaking ? 'bg-emerald-400 animate-pulse' : 'bg-emerald-400'}`} />
                  <span className="font-heading text-sm font-semibold text-white drop-shadow-lg">{simulation.avatar_name || 'AI Partner'}</span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <span className="text-[11px] text-gray-400">{sending ? 'Thinking...' : isSpeaking ? 'Speaking...' : isCompleted ? 'Session ended' : 'Listening'}</span>
                  {currentEmotion !== 'neutral' && !isCompleted && (
                    <Badge data-testid="emotion-badge" className={`text-[9px] px-1.5 py-0 border-none ${EMOTION_BADGE_STYLES[currentEmotion] || 'bg-gray-500/15 text-gray-400'}`}>{emotionCfg.label}</Badge>
                  )}
                </div>

                {/* TTS Waveform */}
                {isSpeaking && (
                  <div className="mt-3 flex items-center justify-center gap-[3px]" data-testid="tts-waveform">
                    {[0.4, 0.7, 1, 0.8, 0.5, 0.9, 0.3, 0.6, 1, 0.7, 0.4, 0.8].map((h, i) => (
                      <div key={i} className="w-[3px] bg-emerald-400 rounded-full animate-pulse" style={{ height: `${8 + h * 16}px`, animationDelay: `${i * 80}ms`, animationDuration: '0.6s' }} />
                    ))}
                  </div>
                )}

                {/* AI Response Bubble */}
                {lastAIMsg && !isCompleted && !sending && (
                  <div className="mt-4 max-w-sm mx-auto hidden sm:block">
                    <div className="bg-black/40 backdrop-blur-xl rounded-xl px-4 py-3 border border-white/10 shadow-lg">
                      <p className="text-sm text-gray-200 leading-relaxed line-clamp-3">{lastAIMsg.content}</p>
                    </div>
                  </div>
                )}

                {sending && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <div className="flex gap-1.5">
                      {[0, 150, 300].map((delay, i) => (
                        <div key={i} className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Self-view PIP */}
            <div className={`absolute bottom-20 lg:bottom-24 right-3 lg:right-6 w-32 h-24 sm:w-40 sm:h-28 rounded-xl overflow-hidden border-2 shadow-2xl z-20 transition-all ${isRecording ? 'border-red-500 shadow-red-500/20' : 'border-white/15'}`}
              data-testid="self-view-pip">
              {vidOn ? (
                <video ref={webcamRef} autoPlay muted playsInline className="w-full h-full object-cover transform scale-x-[-1]" data-testid="webcam-feed" />
              ) : (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  <div className="text-center"><VideoOff className="w-5 h-5 mx-auto text-gray-500 mb-0.5" /><span className="text-[9px] text-gray-500">Camera Off</span></div>
                </div>
              )}
              {isRecording && (
                <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center gap-[2px] h-3 px-1.5 bg-black/50">
                  {[0.3, 0.6, 1, 0.8, 0.5, 0.9, 0.4, 0.7].map((f, i) => (
                    <div key={i} className="w-[3px] bg-red-400 rounded-full transition-all" style={{ height: `${Math.max(2, voiceLevel * f * 12)}px` }} />
                  ))}
                </div>
              )}
              <div className="absolute top-1.5 left-2"><span className="text-[9px] text-white font-medium drop-shadow-md">You</span></div>
            </div>

            {/* Floating Toolbar */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
              <div className="bg-gray-900/80 backdrop-blur-2xl border border-white/10 rounded-full px-3 py-2 flex items-center gap-2 shadow-2xl">
                <button data-testid="mic-toggle" onClick={toggleRecording}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all relative ${isRecording ? 'bg-red-500 text-white ring-2 ring-red-500/50' : micOn ? 'bg-white/10 hover:bg-white/20 text-gray-200' : 'bg-red-500/80 text-white'}`}>
                  {isRecording || micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                  {isRecording && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />}
                </button>
                <button data-testid="video-toggle" onClick={() => setVidOn(!vidOn)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${vidOn ? 'bg-white/10 hover:bg-white/20 text-gray-200' : 'bg-red-500/80 text-white'}`}>
                  {vidOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                </button>
                <div className="w-px h-6 bg-white/10 mx-1" />
                <button data-testid="end-call-button" onClick={() => {
                  stopTTS();
                  if (isRecording) { isRecordingRef.current = false; recognitionRef.current?.stop(); stopVoiceMonitor(); setIsRecording(false); }
                  if (!isCompleted && userMsgCount >= 3) completeSimulation(); else navigate('/');
                }} className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-all shadow-lg shadow-red-500/20">
                  <PhoneOff className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Panel - 4 cols */}
        <div className="col-span-1 lg:col-span-4 flex flex-col h-[45vh] lg:h-full bg-gray-900/60 lg:backdrop-blur-xl border-t lg:border-t-0 lg:border lg:border-white/10 lg:rounded-2xl overflow-hidden min-h-0">
          <div className="px-4 py-2.5 border-b border-white/10 flex-shrink-0 flex items-center justify-between">
            <span className="text-[11px] font-medium tracking-[0.15em] uppercase text-gray-500">Conversation</span>
            <span className="text-[10px] text-gray-600">{messages.length} messages</span>
          </div>

          <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-3 min-h-0">
            {messages.map((msg, i) => (
              <div key={msg.timestamp ? `${msg.role}-${msg.timestamp}-${i}` : `msg-${i}`} data-testid={`chat-message-${i}`}
                className={`${msg.role === 'user' ? 'ml-6 sm:ml-8' : 'mr-6 sm:mr-8'}`}
                style={{ animation: `slideUp 0.3s ease-out ${i * 0.02}s both` }}>
                <div className={`rounded-2xl px-4 py-3 ${msg.role === 'user' ? 'bg-emerald-500/15 border border-emerald-500/20 rounded-tr-sm' : 'bg-white/5 border border-white/10 rounded-tl-sm'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-medium tracking-[0.1em] uppercase ${msg.role === 'user' ? 'text-emerald-400/70' : 'text-gray-500'}`}>
                      {msg.role === 'user' ? 'You' : (simulation.avatar_name || 'AI Partner')}
                    </span>
                    {msg.role === 'assistant' && msg.emotion && msg.emotion !== 'neutral' && (
                      <Badge className={`text-[8px] px-1 py-0 border-none ${EMOTION_BADGE_STYLES[msg.emotion] || 'bg-gray-500/15 text-gray-400'}`}>{(EMOTION_CONFIG[msg.emotion] || EMOTION_CONFIG.neutral).label}</Badge>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed text-gray-200">{msg.content}</p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="mr-6 sm:mr-8">
                <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex gap-1.5">
                    {[0, 150, 300].map((d, i) => (
                      <div key={i} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          {!isCompleted && (
            <div className="border-t border-white/10 px-3 sm:px-4 py-3 pb-24 sm:pb-3 flex-shrink-0 bg-gray-900/80 relative z-[60]">
              {isRecording && (
                <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-xs text-red-400 font-medium flex-1">Listening...</span>
                  <div className="flex items-center gap-[2px] h-4">
                    {[0.4, 0.7, 1, 0.8, 0.5, 0.9, 0.6, 0.3, 0.7, 0.5].map((f, i) => (
                      <div key={i} className="w-[2px] bg-red-400 rounded-full transition-all" style={{ height: `${Math.max(3, voiceLevel * f * 16)}px` }} />
                    ))}
                  </div>
                  <button onClick={toggleRecording} className="text-xs text-red-400 font-semibold hover:text-red-300 ml-2">Stop</button>
                </div>
              )}
              {interimText && (
                <div className="mb-2 px-3 py-1.5 bg-emerald-500/5 rounded-lg border border-emerald-500/10">
                  <p className="text-xs text-gray-400 italic">{interimText}</p>
                </div>
              )}
              <div className="flex items-end gap-2 relative z-10">
                {speechSupported && (
                  <button data-testid="voice-input-button" onClick={toggleRecording}
                    className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all border ${isRecording ? 'bg-red-500 text-white border-red-500 ring-2 ring-red-500/30' : 'bg-white/5 border-white/10 hover:bg-white/10 text-gray-400'}`}>
                    {isRecording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  </button>
                )}
                <textarea ref={inputRef} data-testid="chat-input" value={inputText}
                  onChange={(e) => setInputText(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder={isRecording ? "Speak now..." : "Type a message..."}
                  rows={1}
                  className="flex-1 resize-none min-h-[36px] max-h-[80px] text-sm py-2 px-3 rounded-xl bg-white/5 border border-white/10 text-gray-100 placeholder-gray-500 outline-none focus:border-emerald-500/40 focus:ring-1 focus:ring-emerald-500/20 transition-all"
                  style={{ height: 'auto', overflow: 'hidden' }}
                  onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px'; }}
                />
                <Button data-testid="send-message-button" onClick={() => {
                    if (isRecording) { isRecordingRef.current = false; recognitionRef.current?.stop(); stopVoiceMonitor(); setIsRecording(false); }
                    sendMessage();
                  }}
                  disabled={!inputText.trim() || sending}
                  className="h-9 px-3 bg-emerald-500 text-white flex-shrink-0 gap-1.5 text-xs font-medium rounded-xl hover:bg-emerald-600 disabled:opacity-30 disabled:bg-gray-700 relative z-20 transition-all">
                  <Send className="w-3.5 h-3.5" /> Send
                </Button>
              </div>
              {userMsgCount < 3 && !isRecording && (
                <p className="text-[10px] text-gray-600 mt-1.5">{3 - userMsgCount} more message{3 - userMsgCount !== 1 ? 's' : ''} needed before scoring</p>
              )}
            </div>
          )}
          {isCompleted && (
            <div className="border-t border-white/10 px-4 py-3 bg-gray-900/80 flex-shrink-0">
              <p className="text-xs text-gray-500 text-center">Session completed. View your score or download report.</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function logger_warn(msg, err) {
  if (typeof console !== 'undefined') console.warn(msg, err);
}
